import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { buildPath } from './lib/tenant';
import { useQuery } from '@tanstack/react-query';
import { Container, Header, Segment, Icon, Button, Loader, Dimmer, Message, Popup } from 'semantic-ui-react';
import { QRCodeSVG } from 'qrcode.react';
import moment from 'moment';
import { getOrderDetail } from './lib/tickets';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function OrderConfirmation() {
    const { orderRef } = useParams();
    const [shareCopied, setShareCopied] = useState(false);
    const [qrFullscreen, setQrFullscreen] = useState(null);

    const { data: order, isLoading, isError } = useQuery({
        queryKey: ['order', orderRef],
        queryFn: () => getOrderDetail(orderRef),
    });

    const handleShare = () => {
        const url = window.location.origin + buildPath('/events');
        if (navigator.share) {
            navigator.share({ title: order?.event_name, url }).catch(() => {});
        } else {
            navigator.clipboard.writeText(url).then(() => {
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
            });
        }
    };

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('orderConfirmation.loading')} />
            </Dimmer>
        );
    }

    if (isError || order?.error) {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message negative>
                    <Message.Header>{t('orderConfirmation.errorTitle')}</Message.Header>
                    <p>{t('orderConfirmation.errorBody')}</p>
                </Message>
                <Button as={Link} to={buildPath('/my-tickets')}>{t('orderConfirmation.viewMyTickets')}</Button>
            </Container>
        );
    }

    const firstTicket = order.items?.[0];

    return (
        <>
            {/* Fullscreen QR overlay */}
            {qrFullscreen && (
                <div
                    onClick={() => setQrFullscreen(null)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.92)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 9999, cursor: 'pointer',
                    }}
                >
                    <div style={{
                        background: '#fff', padding: 24, borderRadius: 16,
                        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                    }}>
                        <QRCodeSVG value={qrFullscreen} size={280} level="M" />
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '1.5rem', fontSize: '0.9rem' }}>
                        {t('orderConfirmation.tapToClose')}
                    </p>
                </div>
            )}

            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <Icon
                        name="check circle"
                        size="massive"
                        style={{ color: 'var(--colour_heading_success)' }}
                    />
                    <Header as="h1" className="sectionHeader" style={{ marginTop: '1rem' }}>
                        {t('orderConfirmation.title')}
                    </Header>
                    <p style={{ color: '#555', fontSize: '1.1rem' }}>
                        {t('orderConfirmation.subtitle')}
                    </p>
                </div>

                <Segment style={{ borderRadius: 12, padding: '2rem', marginBottom: '1.5rem' }}>
                    <Header as="h3" style={{ color: 'var(--colour_menu_main)', marginBottom: '1rem' }}>
                        {order.event_name}
                    </Header>

                    {order.event_date && (
                        <p>
                            <Icon name="calendar alternate outline" />
                            {moment(order.event_date).format('dddd D MMMM YYYY, HH:mm')}
                        </p>
                    )}

                    {order.event_location && (
                        <p>
                            <Icon name="map marker alternate" />
                            {order.event_location}
                        </p>
                    )}

                    <p style={{ marginTop: '1rem' }}>
                        <Icon name="ticket" />
                        <strong>{t('orderConfirmation.ticketsCount', { count: order.quantity, plural: order.quantity > 1 ? 's' : '' })}</strong>
                        {' '}— {formatMoney(order.total_amount, order.currency)}
                    </p>

                    <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        {t('orderConfirmation.orderRefLabel')} <strong>{order.order_ref}</strong>
                    </p>
                </Segment>

                {/* First ticket QR — instant access without extra click */}
                {firstTicket && (
                    <Segment style={{ borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                        <p style={{ fontWeight: 600, color: 'var(--colour_menu_main)', marginBottom: '0.75rem' }}>
                            {order.quantity > 1
                                ? t('orderConfirmation.yourTicketOf', { count: order.quantity })
                                : t('orderConfirmation.yourTicket')}
                        </p>
                        <div
                            onClick={() => setQrFullscreen(firstTicket.ticket_code)}
                            style={{
                                display: 'inline-block', padding: 16, background: '#fff',
                                borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                                cursor: 'zoom-in',
                            }}
                        >
                            <QRCodeSVG value={firstTicket.ticket_code} size={160} level="M" />
                        </div>
                        <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#aaa' }}>
                            {t('orderConfirmation.tapToEnlarge')}
                        </p>
                        {order.quantity > 1 && (
                            <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.25rem' }}>
                                {t('orderConfirmation.allTicketsAvailable', { count: order.quantity })}
                            </p>
                        )}
                    </Segment>
                )}

                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <Button
                        as={Link}
                        to={buildPath('/my-tickets')}
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', flex: 1 }}
                    >
                        {t('orderConfirmation.viewMyTickets')}
                    </Button>
                    <Popup
                        content={shareCopied ? t('orderConfirmation.linkCopied') : t('orderConfirmation.shareEventPage')}
                        position="top center"
                        trigger={
                            <Button basic icon="share alternate" content={t('orderConfirmation.share')} onClick={handleShare} style={{ flex: 1 }} />
                        }
                        open={shareCopied || undefined}
                    />
                </div>

                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <Link to={buildPath('/events')} style={{ color: '#888', fontSize: '0.9rem' }}>
                        {t('orderConfirmation.browseMoreEvents')}
                    </Link>
                </div>
            </Container>
        </>
    );
}

export default OrderConfirmation;
